import { useModal } from "@v2/store/modalStore";
import { billingUtils } from "@v2/trpc/routes/billing";
import {
  subscriptionRouter,
  subscriptionUtils,
} from "@v2/trpc/routes/subscription";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { resolveTRPCClientErrorDetails } from "@v2/utils/error-handling";

import { ADDON_APPROVAL_CONFIRMATION_PENDING_KEY } from "../billingToasts";

// The billing error modal is the error UI for these, so the global toast skips them.
const BILLING_MODAL_ERROR_KEYS = ["integrationNotFoundForBilling", "notFound"];

const useApplySubscriptionChangeMutation = ({
  onClose,
  isAddonApprovalConfirmation = false,
}: {
  onClose?: () => void;
  isAddonApprovalConfirmation?: boolean;
} = {}) => {
  const { openModal } = useModal();

  const {
    mutateAsync: applySubscriptionChange,
    isPending,
    data,
  } = subscriptionRouter.applySubscriptionChange.useMutation({
    meta: {
      reploIgnoreDefaultErrorHandlingForKeys: BILLING_MODAL_ERROR_KEYS,
    },
    onSuccess: (data) => {
      void workspaceUtils.getUserWorkspacesList.invalidate();
      void subscriptionUtils.getActiveSubscription.invalidate();
      void workspaceUtils.getPublishedElementsUsage.invalidate();
      void workspaceUtils.getReploSitesUsage.invalidate();
      void workspaceUtils.getShopifyThemeProjectsUsage.invalidate();
      void billingUtils.getNextCorePlanBillingDate.invalidate();

      if (
        window &&
        data.success &&
        data.confirmationRequired &&
        data.confirmationUrl
      ) {
        // Record (or clear) which billing flow this redirect belongs to so the
        // confirmation handler shows the right toast on return. Writing it on
        // every redirect keeps it in sync with the most recent action, so a
        // stale flag from an abandoned add-on approval can't leak into a later
        // unrelated plan change.
        if (isAddonApprovalConfirmation) {
          window.localStorage.setItem(
            ADDON_APPROVAL_CONFIRMATION_PENDING_KEY,
            "true",
          );
        } else {
          window.localStorage.removeItem(
            ADDON_APPROVAL_CONFIRMATION_PENDING_KEY,
          );
        }
        window.location.href = data.confirmationUrl;
      }
    },
    onError: (error) => {
      const errorKey = resolveTRPCClientErrorDetails(error)?.errorKey;
      if (errorKey && BILLING_MODAL_ERROR_KEYS.includes(errorKey)) {
        onClose?.();
        openModal({
          type: "billingErrorModal",
          props: {},
        });
      }
    },
  });

  return {
    applySubscriptionChange,
    isLoading: isPending,
    data,
  };
};

export default useApplySubscriptionChangeMutation;
